import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Pure helpers to decide whether a promo modal campaign is active.
 * @param {import('./promoModalConfig').PromoModalCampaign} campaign
 * @param {import('./promoModalConfig').PromoModalMatchContext} ctx
 * @returns {boolean}
 */
export function campaignMatches(campaign, ctx) {
  if (!campaign || campaign.enabled === false) {
    return false;
  }
  var now = ctx.now instanceof Date ? ctx.now : new Date();
  var scheduleTime = getScheduleTime(now, campaign.timeZone);
  if (campaign.dateRange) {
    var {
      start,
      end
    } = campaign.dateRange;
    if (start || end) {
      var day = scheduleTime.date;
      if (start && day < start) {
        return false;
      }
      if (end && day > end) {
        return false;
      }
    }
  }
  if (campaign.daysOfWeek && campaign.daysOfWeek.length > 0) {
    if (!campaign.daysOfWeek.includes(scheduleTime.dayOfWeek)) {
      return false;
    }
  }
  if (campaign.hoursLocal) {
    if (!isWithinLocalHours(scheduleTime.minutesSinceMidnight, campaign.hoursLocal.start, campaign.hoursLocal.end)) {
      return false;
    }
  }
  if (campaign.pathPatterns && campaign.pathPatterns.length > 0) {
    var path = normalizePathname(ctx.pathname || "");
    var mode = campaign.pathMatchMode || "prefix";
    var ok = campaign.pathPatterns.some(pattern => {
      var p = normalizePathname(pattern);
      if (mode === "exact") {
        return path === p;
      }
      return path === p || path.startsWith(p + "/");
    });
    if (!ok) {
      return false;
    }
  }
  if (campaign.storeLsIds && campaign.storeLsIds.length > 0) {
    var id = ctx.storeLsId;
    if (id == null || !campaign.storeLsIds.includes(Number(id))) {
      return false;
    }
  }
  if (campaign.storeCodes && campaign.storeCodes.length > 0) {
    var storeCode = ctx.storeCode;
    if (!storeCode || !campaign.storeCodes.includes(storeCode)) {
      return false;
    }
  }
  if (campaign.excludeStoreCodes && campaign.excludeStoreCodes.length > 0) {
    var _storeCode = ctx.storeCode;
    if (_storeCode && campaign.excludeStoreCodes.includes(_storeCode)) {
      return false;
    }
  }
  if (campaign.excludeStoreLsIds && campaign.excludeStoreLsIds.length > 0) {
    var _id = ctx.storeLsId;
    if (_id != null && campaign.excludeStoreLsIds.includes(Number(_id))) {
      return false;
    }
  }
  if (campaign.siteStoreViewCodes && campaign.siteStoreViewCodes.length > 0) {
    var storeViewOrder = ctx.storeViewOrder || ctx.siteStoreViewCode;
    if (!storeViewOrder || !campaign.siteStoreViewCodes.includes(storeViewOrder)) {
      return false;
    }
  }
  if (campaign.excludeStoreViewOrders && campaign.excludeStoreViewOrders.length > 0) {
    var _storeViewOrder = ctx.storeViewOrder || ctx.siteStoreViewCode;
    if (_storeViewOrder && campaign.excludeStoreViewOrders.includes(_storeViewOrder)) {
      return false;
    }
  }
  if (campaign.guestsOnly && ctx.isSignedIn) {
    return false;
  }
  if (campaign.locales && campaign.locales.length > 0) {
    var language = normalizeLanguage(ctx.language);
    if (!language || !campaign.locales.map(normalizeLanguage).includes(language)) {
      return false;
    }
  }
  if (campaign.primaryCta && campaign.primaryCta.action === "add_to_cart" && campaign.primaryCta.needStock) {
    var sku = campaign.primaryCta.sku;
    var availableSkus = Array.isArray(ctx.availableSkus) ? ctx.availableSkus : [];
    if (!sku || !availableSkus.includes(sku)) {
      return false;
    }
  }
  return true;
}

/**
 * @param {import('./promoModalConfig').PromoModalCampaign[]} campaigns
 * @param {import('./promoModalConfig').PromoModalMatchContext} ctx
 * @returns {import('./promoModalConfig').PromoModalCampaign | null}
 */
export function pickFirstMatchingCampaign(campaigns, ctx) {
  if (!Array.isArray(campaigns)) {
    return null;
  }
  var sorted = [...campaigns].sort((a, b) => {
    var pa = a.priority != null ? a.priority : 100;
    var pb = b.priority != null ? b.priority : 100;
    return pa - pb;
  });
  for (var c of sorted) {
    if (campaignMatches(c, ctx)) {
      return c;
    }
  }
  return null;
}
function getScheduleTime(date, timeZone) {
  if (!timeZone) {
    return {
      date: "".concat(date.getFullYear(), "-").concat(String(date.getMonth() + 1).padStart(2, "0"), "-").concat(String(date.getDate()).padStart(2, "0")),
      dayOfWeek: date.getDay(),
      minutesSinceMidnight: date.getHours() * 60 + date.getMinutes()
    };
  }
  try {
    var parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(date).reduce((result, part) => _objectSpread(_objectSpread({}, result), {}, {
      [part.type]: part.value
    }), {});
    var dayOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
    return {
      date: "".concat(parts.year, "-").concat(parts.month, "-").concat(parts.day),
      dayOfWeek,
      minutesSinceMidnight: Number(parts.hour) * 60 + Number(parts.minute)
    };
  } catch (_unused) {
    return getScheduleTime(date);
  }
}
function parseHm(hm) {
  if (!hm || typeof hm !== "string") {
    return null;
  }
  var [h, m] = hm.split(":").map(x => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }
  return h * 60 + m;
}
function isWithinLocalHours(currentMinutes, startHm, endHm) {
  var start = parseHm(startHm);
  var end = parseHm(endHm);
  if (start == null || end == null) {
    return true;
  }
  if (start <= end) {
    return currentMinutes >= start && currentMinutes <= end;
  }
  // window across midnight
  return currentMinutes >= start || currentMinutes <= end;
}
function normalizePathname(pathname) {
  if (!pathname) {
    return "/";
  }
  var p = pathname;
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }
  return p;
}
function normalizeLanguage(language) {
  if (!language || typeof language !== "string") {
    return null;
  }
  return language.trim().toLowerCase();
}